import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { SectionHeader } from '../../components/PageHeader';
import { useToast } from '../../toast';

interface Certificate {
  subject: string;
  notBefore: string;
  notAfter: string;
  fingerprint: string;
}

interface Canary {
  at: string;
  ok: boolean | null;
  detail: string | null;
  org: string | null;
}

interface SigningKey {
  kid: string;
  source: 'mounted' | 'uploaded';
  active: boolean;
  previous: boolean;
  fingerprint: string;
  bits: number | null;
  certificate: Certificate | null;
  uploadedAt: string | null;
  uploadedBy: string | null;
  activatedAt: string | null;
  activatedBy: string | null;
  canary: Canary | null;
}

interface CanaryOrg {
  id: string;
  label: string;
  environment: string;
  workspace: string;
}

interface Page {
  keys: SigningKey[];
  active: string;
  previous: string | null;
  canaryOrgs: CanaryOrg[];
}

const short = (fingerprint: string) => fingerprint.slice(0, 16);

/**
 * Signing keys, and the rotation they exist for.
 *
 * The order of operations is the whole point of this screen, because getting it wrong takes every customer
 * org down for the four minutes it takes to notice. Upload the key here, deploy its certificate to the
 * home org, wait, check a real org with the canary, and only then activate. Nothing you do before the last
 * step changes what anybody's session is signed with.
 */
export function SigningKeys() {
  const [page, setPage] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [kid, setKid] = useState('');
  const [privateKeyPem, setPrivateKeyPem] = useState('');
  const [certificatePem, setCertificatePem] = useState('');
  const [canaryOrg, setCanaryOrg] = useState('');
  const [canaryFor, setCanaryFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await api<Page>('/api/admin/signing-keys');
      setPage(next);
      setCanaryOrg((current) => current || (next.canaryOrgs[0]?.id ?? ''));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read the signer');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(what: () => Promise<string>) {
    setBusy(true);
    setError(null);
    try {
      toast.ok(await what());
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  function upload(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await api('/api/admin/signing-keys', {
        method: 'POST',
        body: { kid, privateKeyPem, certificatePem: certificatePem || undefined },
      });
      // Held only as long as the request needed it. The signer has the key now; this page should not.
      setPrivateKeyPem('');
      setCertificatePem('');
      setKid('');
      setShowUpload(false);
      return 'Key stored. Deploy its certificate to the home org, then run the canary before activating.';
    });
  }

  // The heading stands whether or not the signer answered, so a failure reads as this page's failure
  // rather than as an empty space under the console's title.
  const header = (
    <SectionHeader
      title="Signing keys"
      blurb="The key every session is signed with, and the order to replace it in without taking customer orgs down."
    />
  );

  if (!page) {
    return (
      <>
        {header}
        {error ? <div className="alert">{error}</div> : <p className="muted">Loading…</p>}
      </>
    );
  }

  return (
    <>
      {header}
      {error && <div className="alert">{error}</div>}

      <div className="card">
        <h3>How a rotation goes</h3>
        <ol className="small muted" style={{ lineHeight: 1.7, marginBottom: 0 }}>
          <li>Upload the new key and its certificate below. Nothing changes yet.</li>
          <li>
            Deploy the certificate to the KruviSolutions home org (<span className="mono">salesforce/home-org</span>).
            The consumer key does not change.
          </li>
          <li>
            Wait. A certificate takes three and a half to four minutes to reach subscriber orgs, and there is
            no moment when both keys work.
          </li>
          <li>Run the canary against a real org until it passes. That is the proof, not the clock.</li>
          <li>Activate. The key it replaces stays available, so a mint caught mid-flip retries with it.</li>
        </ol>
      </div>

      <h2>Keys</h2>
      {/* Above the table rather than below it: this is the org every Canary button in the rows checks
          against, so it is the thing to read before pressing one. */}
      <div className="row" style={{ marginBottom: 16 }}>
        <label className="small">
          Canary against
          <select value={canaryOrg} onChange={(e) => setCanaryOrg(e.target.value)}>
            {page.canaryOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.workspace} · {o.label} ({o.environment})</option>
            ))}
          </select>
        </label>
        {!page.canaryOrgs.length && (
          <span className="small muted">
            No org has a verified connection to check against, so a rotation here cannot be proved before it
            is made.
          </span>
        )}
      </div>

      <div className="card flush">
        <table>
          <thead>
            <tr><th>Key</th><th>Fingerprint</th><th>Certificate</th><th>Last canary</th><th className="actions-cell"></th></tr>
          </thead>
          <tbody>
            {page.keys.length === 0 && (
              <tr><td colSpan={5} className="empty">The signer holds no keys.</td></tr>
            )}
            {page.keys.map((k) => (
              <tr key={k.kid}>
                <td>
                  <span className="mono">{k.kid}</span>
                  <br />
                  {k.active && <span className="pill on">in use</span>}{' '}
                  {k.previous && <span className="pill">fallback</span>}{' '}
                  {k.source === 'mounted' && <span className="pill">mounted</span>}
                  <br />
                  <span className="small muted">
                    {k.bits ? `${k.bits}-bit` : ''}
                    {k.uploadedBy ? ` · uploaded by ${k.uploadedBy}` : ''}
                    {k.activatedAt ? ` · in use since ${new Date(k.activatedAt).toLocaleString()}` : ''}
                  </span>
                </td>
                <td className="small mono">{short(k.fingerprint)}…</td>
                <td className="small">
                  {k.certificate ? (
                    <>
                      expires {new Date(k.certificate.notAfter).toLocaleDateString()}
                      <br />
                      {/* A real link styled as a button: the browser needs the href to save the file from. */}
                      <a
                        className="button secondary"
                        href={`/api/admin/signing-keys/${k.kid}/certificate`}
                        style={{ marginTop: 6 }}
                      >
                        Download for the home org
                      </a>
                    </>
                  ) : (
                    <span className="muted">none uploaded</span>
                  )}
                </td>
                <td className="small">
                  {k.canary ? (
                    <>
                      <span className={k.canary.ok ? 'pill on' : 'pill off'}>{k.canary.ok ? 'accepted' : 'refused'}</span>
                      <br />
                      <span className="muted">
                        {new Date(k.canary.at).toLocaleString()}
                        {k.canary.org ? ` · ${k.canary.org}` : ''}
                      </span>
                      {!k.canary.ok && k.canary.detail && <><br /><span className="muted">{k.canary.detail}</span></>}
                    </>
                  ) : (
                    <span className="muted">never checked</span>
                  )}
                </td>
                <td className="actions-cell">
                  <button
                    className="secondary"
                    disabled={busy || !canaryOrg}
                    onClick={() => {
                      setCanaryFor(k.kid);
                      void run(async () => {
                        const result = await api<{ ok: boolean; detail: string; org: string }>(
                          `/api/admin/signing-keys/${k.kid}/canary`,
                          { method: 'POST', body: { customerOrgId: canaryOrg } },
                        );
                        setCanaryFor(null);
                        return result.ok
                          ? `${result.org} accepted ${k.kid}. Safe to activate.`
                          : `${result.org} refused ${k.kid}: ${result.detail}`;
                      });
                    }}
                  >
                    {busy && canaryFor === k.kid ? 'Checking…' : 'Canary'}
                  </button>{' '}
                  {!k.active && (
                    <button
                      disabled={busy}
                      onClick={() => {
                        const warning = k.canary?.ok
                          ? `Sign every new session with ${k.kid}?`
                          : `${k.kid} has not passed a canary check. Activating it now will break every mint if its certificate has not reached the customer orgs yet. Continue?`;
                        if (!confirm(warning)) return;
                        void run(async () => {
                          await api(`/api/admin/signing-keys/${k.kid}/activate`, { method: 'POST' });
                          return `${k.kid} is now the signing key.`;
                        });
                      }}
                    >
                      Activate
                    </button>
                  )}{' '}
                  {!k.active && k.source === 'uploaded' && (
                    <button
                      className="danger"
                      disabled={busy}
                      onClick={() => {
                        if (!confirm(`Delete ${k.kid} from the signer? This cannot be undone.`)) return;
                        void run(async () => {
                          await api(`/api/admin/signing-keys/${k.kid}`, { method: 'DELETE' });
                          return `${k.kid} removed.`;
                        });
                      }}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Add a key</h2>
      {!showUpload ? (
        <div className="actions">
          <button className="secondary" onClick={() => setShowUpload(true)}>Upload a new key</button>
        </div>
      ) : (
        <form className="card" onSubmit={upload}>
          <p className="small muted" style={{ margin: 0 }}>
            The private key is passed straight to the signer and is not stored here, written to disk or
            logged. What this database keeps is its fingerprint and the certificate, both of which are
            public. Generate a pair with <span className="mono">scripts/gen-dev-key.sh</span> if you need one.
          </p>
          <label>
            Key id
            <input
              value={kid}
              onChange={(e) => setKid(e.target.value)}
              placeholder="2026-09-rotation"
              pattern="[a-z0-9][a-z0-9._\-]*"
              required
            />
          </label>
          <label>
            Private key (PEM)
            <textarea
              className="mono small"
              value={privateKeyPem}
              onChange={(e) => setPrivateKeyPem(e.target.value)}
              rows={6}
              required
              spellCheck={false}
              autoComplete="off"
              placeholder="-----BEGIN PRIVATE KEY-----"
            />
          </label>
          <label>
            Certificate (PEM)
            <span className="small muted" style={{ fontWeight: 400 }}>
              Optional, but without it nothing here can check that the certificate you deploy to the home org
              belongs to this key.
            </span>
            <textarea
              className="mono small"
              value={certificatePem}
              onChange={(e) => setCertificatePem(e.target.value)}
              rows={6}
              spellCheck={false}
              autoComplete="off"
              placeholder="-----BEGIN CERTIFICATE-----"
            />
          </label>
          <button type="submit" className="block" disabled={busy || !kid || !privateKeyPem}>Store the key</button>
          <button
            type="button"
            className="secondary block"
            onClick={() => {
              setShowUpload(false);
              setPrivateKeyPem('');
              setCertificatePem('');
            }}
          >
            Cancel
          </button>
        </form>
      )}
    </>
  );
}
